import { getSessionManager } from '@/lib/SessionTracking/SessionManager';
import {
  MutationCache,
  QueryCache,
  QueryClientConfig,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { getAxiosErrorMessage } from 'lib/react-query/axios';

const formatError = (handlerName: 'Query' | 'Mutation', error: unknown): void =>
  console.error(
    `Global ${handlerName} error handler. `,
    'Message:',
    getAxiosErrorMessage(error as AxiosError),
    'Error object:',
    error
  );

type MutationErrorMeta = {
  logError?: boolean;
  severity?: string;
  suppressExpectedErrorLogging?: boolean;
};

const isExpectedHandledMutationError = (
  error: unknown,
  meta: MutationErrorMeta | undefined
): boolean => {
  const status = (error as AxiosError | undefined)?.response?.status;
  return (
    meta?.suppressExpectedErrorLogging === true &&
    typeof status === 'number' &&
    status >= 400 &&
    status < 500
  );
};

/**
 * important: this must be function and not object literal
 * so constructors (new QueryCache ...) are invoked for each test
 * to prevent shared cache between tests
 */
const getQueryClientConfig = (): QueryClientConfig => ({
  defaultOptions: {
    queries: {
      throwOnError: false,
      refetchOnWindowFocus: false,

      staleTime: 1000,
      retry: (failureCount, _error) => {
        const error = _error as AxiosError;
        if (error?.response?.status === 401) {
          // if input data is wrong or you're not authorized there's no point retrying a query
          return false;
        }
        const MAX_QUERY_RETRIES = 3;
        return failureCount < MAX_QUERY_RETRIES;
      },
    },
    mutations: {
      throwOnError: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => formatError('Query', error),
  }),
  mutationCache: new MutationCache({
    onError: async (error, _variables, _context, mutation) => {
      // NEW: Log critical mutations to database
      const criticalMutations = [
        'createAssignment',
        'submitAssignment',
        'saveNotebookEntry',
        'createClassroom',
        'deleteUser',
      ];

      const mutationKey = mutation.options.mutationKey;
      const keyParts = Array.isArray(mutationKey)
        ? mutationKey.map(String)
        : typeof mutationKey === 'string'
          ? [mutationKey]
          : [];
      const meta = mutation.options.meta as MutationErrorMeta | undefined;
      const suppressExpectedErrorLogging = isExpectedHandledMutationError(
        error,
        meta
      );
      if (!suppressExpectedErrorLogging) {
        console.error('Mutation error:', error);
      }
      const matchesCritical = keyParts.some((key) =>
        criticalMutations.includes(key)
      );
      const shouldLog =
        typeof window !== 'undefined' &&
        !suppressExpectedErrorLogging &&
        (meta?.logError ?? matchesCritical);

      if (!shouldLog) {
        return;
      }

      const sessionManager = getSessionManager();

      if (sessionManager) {
        await sessionManager.flushBufferedEvents();
      }

      // Legacy error logging removed; keep behavior no-op beyond breadcrumb flush.
    },
  }),
});

export default getQueryClientConfig;
